import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Box, Typography, Stack } from "@mui/material";
import { CheckCircleOutlineRounded as CheckIcon } from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../theme";
import CommonDialog from "./CommonDialog";
import CommonButton from "../inputs/CommonButton";

const SuccessPopup = ({
  show,
  open,
  setShowModal,
  onClose,
  redirectPath,
  message = "Operation completed successfully!",
  title = "Success!",
  description,
  btnName = "Continue",
  onConfirm,
  showCloseButton = true,
}) => {
  const history = useHistory();
  const isOpen = Boolean(show !== undefined ? show : open);

  const handleClose = () => {
    if (typeof setShowModal === "function") {
      setShowModal(false);
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleAction = () => {
    handleClose();
    if (typeof onConfirm === "function") {
      onConfirm();
    }
    if (redirectPath) {
      history.push(redirectPath);
    }
  };

  return (
    <CommonDialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      dividers={false}
      showCloseButton={showCloseButton}
      PaperProps={{
        sx: {
          borderRadius: borderRadiusPx.xl,
          p: { xs: 2, sm: 2.5 },
          textAlign: "center",
        },
      }}
    >
      <Box sx={{ pt: 1, pb: 1, px: { xs: 1, sm: 2 } }}>
        {/* Success Icon Badge */}
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: borderRadiusPx.circle,
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
            boxShadow: "0 8px 22px rgba(16, 185, 129, 0.35)",
          }}
        >
          <CheckIcon sx={{ fontSize: 38 }} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: colors.slate[900],
            mb: 1,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            color: colors.slate[600],
            fontWeight: 500,
            lineHeight: 1.5,
            mb: description ? 1 : 2.5,
          }}
        >
          {message}
        </Typography>

        {/* Optional Description */}
        {description && (
          <Typography
            variant="body2"
            sx={{
              color: colors.slate[400],
              lineHeight: 1.45,
              mb: 2.5,
            }}
          >
            {description}
          </Typography>
        )}

        {/* Action CTA Button */}
        <Box sx={{ mt: 1 }}>
          <CommonButton
            fullWidth
            variant="primary"
            size="large"
            onClick={redirectPath || onConfirm ? handleAction : handleClose}
            label={redirectPath ? btnName || "Go to Profile" : btnName || "Close"}
            sx={{
              py: 1.35,
              fontWeight: 700,
              fontSize: "0.95rem",
              borderRadius: borderRadiusPx.lg,
            }}
          />
        </Box>
      </Box>
    </CommonDialog>
  );
};

SuccessPopup.propTypes = {
  show: PropTypes.bool,
  open: PropTypes.bool,
  setShowModal: PropTypes.func,
  onClose: PropTypes.func,
  redirectPath: PropTypes.string,
  message: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  btnName: PropTypes.string,
  onConfirm: PropTypes.func,
  showCloseButton: PropTypes.bool,
};

export default React.memo(SuccessPopup);
